import React from 'react'
import PropTypes from 'prop-types'

import { Form, Group, Input, Checkbox, Select, Tags, RichText } from '@peracto/peracto-ui'
import InputRows from './InputRows'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'

import { StyledList } from './styled'

const TextList = ({ values, help, label }) => {
    let value = []
    if (isString(values)) {
        value.push(values)
    } else if (isObject(values) && !isArray(values)) {
        for (const [key, val] of Object.entries(values)) {
            if (val) {
                value.push(`${key}: ${JSON.stringify(val)}`)
            }
        }
    } else {
        value = values
    }

    return (
        <>
            {label && <label className="!mb-1">{label}</label>}
            {help && <p className="help-text text-grey">{help}</p>}

            {value.length > 0 ? (
                <StyledList>
                    {value.map((item, idx) => (
                        <li key={`${item}_${idx}`}>{item}</li>
                    ))}
                </StyledList>
            ) : (
                <p className="text-grey">No values set for this field.</p>
            )}
        </>
    )
}

const ConfigForm = ({ values, readOnly, ...props }) => {
    return (
        <Form autoComplete="off" values={values} {...props}>
            {Object.keys(values.groups).map(group => (
                <Group
                    key={`${values.groups[group].name}-config`}
                    id={`${values.groups[group].name}-config`}
                    name={values.groups[group].name}
                >
                    {Object.keys(values.groups[group].values).map((val, idx) => {
                        const value = values.groups[group].values[val]
                        const fieldName = `values[${val}]`
                        const label = value.name

                        const isReadOnly = value?.read_only || readOnly

                        let input
                        let inputType = 'text'

                        switch (value.data_type) {
                            case 'rich_text':
                                input = (
                                    <RichText
                                        name={fieldName}
                                        label={label}
                                        help={value.description ? value.description : false}
                                        disabled={isReadOnly}
                                    />
                                )
                                break
                            case 'boolean':
                                input = (
                                    <Checkbox
                                        name={fieldName}
                                        label={label}
                                        help={value.description ? value.description : false}
                                        disabled={isReadOnly}
                                    />
                                )
                                break

                            case 'array':
                                if (value?.input?.type === 'input_rows') {
                                    input = (
                                        <InputRows
                                            name={fieldName}
                                            label={label}
                                            description={value?.description}
                                            fields={value?.input?.fields}
                                        />
                                    )
                                } else {
                                    if (isReadOnly) {
                                        input = (
                                            <TextList
                                                values={values?.values[val]}
                                                label={label}
                                                help={value.description}
                                            />
                                        )
                                    } else {
                                        input = (
                                            <Tags
                                                name={fieldName}
                                                label={label}
                                                options={
                                                    value.input.options
                                                        ? Object.values(value.input.options).map(
                                                              opt => ({
                                                                  label: opt,
                                                                  value: opt,
                                                              })
                                                          )
                                                        : []
                                                }
                                                help={value.description ? value.description : false}
                                                disabled={isReadOnly}
                                            />
                                        )
                                    }
                                }

                                break

                            case 'int':
                            case 'float':
                                inputType = 'number'
                            // falls through: numbers are rendered by the default input below

                            default:
                                if (value?.input?.options) {
                                    // ...then it's a <select>

                                    if (isReadOnly) {
                                        input = (
                                            <TextList
                                                values={values?.values[val]}
                                                label={label}
                                                help={value.description}
                                            />
                                        )
                                    } else {
                                        input = (
                                            <Select
                                                name={fieldName}
                                                label={label}
                                                options={Object.values(value.input.options).map(
                                                    opt => ({
                                                        label: opt,
                                                        value: opt,
                                                    })
                                                )}
                                                help={value.description ? value.description : false}
                                                disabled={isReadOnly}
                                            />
                                        )
                                    }
                                } else {
                                    if (isReadOnly) {
                                        input = (
                                            <TextList
                                                values={values?.values[val]}
                                                label={label}
                                                help={value.description}
                                            />
                                        )
                                    } else {
                                        input = (
                                            <Input
                                                name={fieldName}
                                                label={label}
                                                type={inputType}
                                                help={value.description ? value.description : false}
                                                disabled={isReadOnly}
                                            />
                                        )
                                    }
                                }
                        }

                        return <React.Fragment key={`${group}.${val}`}>{input}</React.Fragment>
                    })}
                </Group>
            ))}
        </Form>
    )
}

ConfigForm.propTypes = {
    values: PropTypes.object.isRequired,
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default ConfigForm
